import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Snackbar,
  Alert,
  Drawer,
  Divider,
  InputAdornment,
  Avatar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility,
  Search,
  Language,
  Email,
  Phone,
  Person,
  Description,
  PhotoCamera,
  Close,
} from '@mui/icons-material';
import AddCompanyModal from '../../components/companies/AddCompanyModal';
import { useSelector } from 'react-redux';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { companyService, userService } from '../../services/api';

const NAVBAR_HEIGHT = 64;

const Companies = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentCompany, setCurrentCompany] = useState(null);
  const editLogoInputRef = useRef(null);

  const handleEditLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { alert('Image must be smaller than 3MB'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setCurrentCompany(prev => ({ ...prev, logo: ev.target.result }));
    reader.readAsDataURL(file);
  };
  const [companyToDelete, setCompanyToDelete] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [coaches, setCoaches] = useState([]);
  const [viewCompany, setViewCompany] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const { user: currentUser } = useSelector((state) => state.auth);
  const canManage = ['superAdmin', 'coach'].includes(currentUser?.role);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const response = await companyService.getCompanies();
      setCompanies(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch companies');
    } finally {
      setLoading(false);
    }
  };

  const fetchCoaches = async () => {
    try {
      const response = await userService.getUsers();
      setCoaches(response.data.filter(u => u.role === 'coach'));
    } catch (err) {
      console.error('Error fetching coaches:', err);
    }
  };

  useEffect(() => {
    fetchCompanies();
    if (currentUser?.role === 'superAdmin') fetchCoaches();
  }, [currentUser]);

  const filteredCompanies = useMemo(() => {
    if (!search.trim()) return companies;
    const q = search.toLowerCase();
    return companies.filter(c =>
      c.name?.toLowerCase().includes(q) ||
      c.contactEmail?.toLowerCase().includes(q) ||
      c.website?.toLowerCase().includes(q)
    );
  }, [companies, search]);

  const paginatedCompanies = useMemo(() =>
    filteredCompanies.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredCompanies, page, rowsPerPage]
  );

  const handleSearchChange = (e) => { setSearch(e.target.value); setPage(0); };
  const handleChangePage = (_, p) => setPage(p);
  const handleChangeRowsPerPage = (e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); };

  const handleAddCompany = async (companyData) => {
    try {
      setLoading(true);
      await companyService.createCompany(companyData);
      await fetchCompanies();
      setOpenAddModal(false);
      setSuccessMessage('Company created successfully');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create company');
    } finally {
      setLoading(false);
    }
  };

  const handleEditCompany = async () => {
    try {
      setLoading(true);
      await companyService.updateCompany(currentCompany._id, currentCompany);
      setSuccessMessage('Company updated successfully');
      setOpenEditModal(false);
      fetchCompanies();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update company');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCompany = async () => {
    try {
      setLoading(true);
      await companyService.deleteCompany(companyToDelete._id);
      setSuccessMessage('Company deleted successfully');
      setOpenDeleteDialog(false);
      fetchCompanies();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete company');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <Container maxWidth="xl">

      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">Companies</Typography>
        {canManage && (
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => setOpenAddModal(true)}>
            Add Company
          </Button>
        )}
      </Box>

      {/* Search */}
      <Box sx={{ mb: 2 }}>
        <TextField
          placeholder="Search by name, email or website…"
          value={search}
          onChange={handleSearchChange}
          size="small"
          sx={{ width: 340 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Website</TableCell>
              <TableCell>Contact Email</TableCell>
              <TableCell>Contact Phone</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedCompanies.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="textSecondary">
                    {companies.length === 0 ? 'No companies found.' : 'No companies match your search.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedCompanies.map((company) => (
                <TableRow key={company._id} hover>
                  <TableCell>{company.name}</TableCell>
                  <TableCell>
                    {company.website
                      ? <a href={company.website} target="_blank" rel="noreferrer" style={{ color: '#1990bb' }}>{company.website}</a>
                      : '—'}
                  </TableCell>
                  <TableCell>{company.contactEmail || '—'}</TableCell>
                  <TableCell>{company.contactPhone || '—'}</TableCell>
                  <TableCell>{new Date(company.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="View Details">
                      <IconButton size="small" onClick={() => setViewCompany(company)}>
                        <Visibility fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {canManage && (
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => { setCurrentCompany(company); setOpenEditModal(true); }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canManage && (
                      <Tooltip title="Delete">
                        <IconButton size="small" sx={{ color: 'error.main' }} onClick={() => { setCompanyToDelete(company); setOpenDeleteDialog(true); }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredCompanies.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Rows:"
        />
      </TableContainer>

      {/* View Details Drawer */}
      <Drawer
        anchor="right"
        open={Boolean(viewCompany)}
        onClose={() => setViewCompany(null)}
        sx={{ '& .MuiDrawer-paper': { top: NAVBAR_HEIGHT, height: `calc(100% - ${NAVBAR_HEIGHT}px)`, width: 340, p: 3 } }}
      >
        {viewCompany && (
          <Box>
            {viewCompany.logo && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Avatar
                  src={viewCompany.logo}
                  variant="rounded"
                  sx={{ width: 160, height: 'auto', '& img': { objectFit: 'contain', height: 'auto' } }}
                />
              </Box>
            )}
            <Typography variant="h6" sx={{ mb: 0.5 }}>{viewCompany.name}</Typography>
            {viewCompany.description && (
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>{viewCompany.description}</Typography>
            )}
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {viewCompany.website && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Language sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <a href={viewCompany.website} target="_blank" rel="noreferrer" style={{ color: '#1990bb', fontSize: 14 }}>
                    {viewCompany.website}
                  </a>
                </Box>
              )}
              {viewCompany.contactEmail && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Email sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="body2">{viewCompany.contactEmail}</Typography>
                </Box>
              )}
              {viewCompany.contactPhone && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Phone sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="body2">{viewCompany.contactPhone}</Typography>
                </Box>
              )}
              {viewCompany.admin && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Person sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="body2">Admin: {viewCompany.admin?.name || viewCompany.admin}</Typography>
                </Box>
              )}
              {viewCompany.description && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Description sx={{ fontSize: 18, color: 'text.secondary', mt: 0.2 }} />
                  <Typography variant="body2">{viewCompany.description}</Typography>
                </Box>
              )}
              <Divider />
              <Box>
                <Typography variant="caption" color="textSecondary">Created</Typography>
                <Typography variant="body2">
                  {new Date(viewCompany.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Drawer>

      {/* Add Company Modal */}
      <AddCompanyModal
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
        onSubmit={handleAddCompany}
      />

      {/* Edit Company Dialog */}
      <Dialog open={openEditModal} onClose={() => setOpenEditModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Company</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Logo upload */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                src={currentCompany?.logo || undefined}
                variant="rounded"
                sx={{ width: 72, height: 72, bgcolor: 'grey.200' }}
              >
                {!currentCompany?.logo && <PhotoCamera sx={{ color: 'grey.500' }} />}
              </Avatar>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Company Logo (optional)</Typography>
                <Button variant="outlined" size="small" startIcon={<PhotoCamera />} onClick={() => editLogoInputRef.current?.click()}>
                  {currentCompany?.logo ? 'Change' : 'Upload'}
                </Button>
                {currentCompany?.logo && (
                  <IconButton size="small" onClick={() => setCurrentCompany(prev => ({ ...prev, logo: '' }))} sx={{ ml: 1 }}>
                    <Close fontSize="small" />
                  </IconButton>
                )}
                <input ref={editLogoInputRef} type="file" accept="image/*" hidden onChange={handleEditLogoChange} />
              </Box>
            </Box>
            <TextField
              fullWidth label="Name"
              defaultValue={currentCompany?.name || ''}
              onChange={(e) => setCurrentCompany({ ...currentCompany, name: e.target.value })}
            />
            <TextField
              fullWidth label="Website" type="url"
              defaultValue={currentCompany?.website || ''}
              onChange={(e) => setCurrentCompany({ ...currentCompany, website: e.target.value })}
            />
            <TextField
              fullWidth label="Contact Email" type="email"
              defaultValue={currentCompany?.contactEmail || ''}
              onChange={(e) => setCurrentCompany({ ...currentCompany, contactEmail: e.target.value })}
            />
            <TextField
              fullWidth label="Contact Phone"
              defaultValue={currentCompany?.contactPhone || ''}
              onChange={(e) => setCurrentCompany({ ...currentCompany, contactPhone: e.target.value })}
            />
            {currentUser?.role === 'superAdmin' && (
              <FormControl fullWidth>
                <InputLabel>Coach</InputLabel>
                <Select
                  value={currentCompany?.coach?._id || ''}
                  label="Coach"
                  onChange={(e) => setCurrentCompany({ ...currentCompany, coach: e.target.value })}
                >
                  {coaches.map((coach) => (
                    <MenuItem key={coach._id} value={coach._id}>{coach.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditModal(false)}>Cancel</Button>
          <Button onClick={handleEditCompany} variant="contained" color="primary" disabled={loading}>
            {loading ? 'Saving…' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Delete Company</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{companyToDelete?.name}</strong>? This cannot be undone.
          </Typography>
          {companyToDelete?.admin && (
            <Typography color="error" sx={{ mt: 1 }}>
              Warning: This company has an admin. Deleting it will remove their admin role.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteCompany} variant="contained" color="error" disabled={loading}>
            {loading ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbars */}
      <Snackbar open={!!successMessage} autoHideDuration={4000} onClose={() => setSuccessMessage('')}>
        <Alert onClose={() => setSuccessMessage('')} severity="success">{successMessage}</Alert>
      </Snackbar>
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert onClose={() => setError(null)} severity="error">{error}</Alert>
      </Snackbar>

    </Container>
  );
};

export default Companies;
